<template>
  <div class="ac-card-content">
    <div class="ac-header-container">
      <button
        type="button"
        class="ac-close-button"
        @click="toggle(false)"
      >
        <img
          src="@shared/assets/img/general/x-black.png"
          alt="x"
          class="ac-close"
        >
      </button>
      <div class="ac-header">
        Slack Notifications
      </div>
    </div>
    <div class="ac-body-container">
      <div class="c-container">
        <div class="c-sub">
          Customize your SmartAC.com notification preferences for
          Slack integration and channel to send below.
        </div>
        <div class="c-sub-header">
          Channel
        </div>
        <div class="c-input-container">
          <div class="c-row-container">
            <div class="c-column-container">
              <select
                v-model="selectedChannelId"
                aria-label="Select Channel"
                class="c-select"
              >
                <option
                  v-for="channel in channelList"
                  :key="channel.id"
                  :value="channel.id"
                >
                  {{ channel.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div class="c-sub-header">
          Notifications
        </div>
        <div class="c-checkbox-container">
          <input
            id="service-check"
            :checked="service !== ''"
            type="checkbox"
            aria-label="Service Requested"
            class="c-checkbox"
            @change="handleCheckboxChange($event, 'service')"
          >
          <div class="c-column-container">
            <label
              class="c-checkbox-label"
              for="service-check"
            >
              Service Requested
            </label>
            <div class="c-checkbox-body">
              Receive notifications in Slack for new service requests from members.
            </div>
          </div>
        </div>
        <div class="c-checkbox-container">
          <input
            id="lead-check"
            type="checkbox"
            aria-label="Lead Created"
            class="c-checkbox"
            :checked="lead !== ''"
            @change="handleCheckboxChange($event, 'lead')"
          >
          <div class="c-column-container">
            <label
              class="c-checkbox-label"
              for="lead-check"
            >
              Lead Created
            </label>
            <div class="c-checkbox-body">
              Enable notifications for new leads to be seamlessly delivered to your Slack workspace.
            </div>
          </div>
        </div>
        <div class="c-checkbox-container">
          <input
            id="system-check"
            type="checkbox"
            aria-label="New System Insights"
            class="c-checkbox"
            :checked="system !== ''"
            @change="handleCheckboxChange($event, 'system')"
          >
          <div class="c-column-container">
            <label
              class="c-checkbox-label"
              for="system-check"
            >
              New System Insights
            </label>
            <div class="c-checkbox-body">
              Get alerts on trend run time, failure to maintain return air
              temperature, short cycle time and high
              discharge air temperature.
            </div>
          </div>
        </div>
        <div class="c-checkbox-container">
          <input
            id="homeowner-check"
            type="checkbox"
            aria-label="Homeowner Notifications Sent"
            class="c-checkbox"
            :checked="homeowner !== ''"
            @change="handleCheckboxChange($event, 'homeowner')"
          >
          <div class="c-column-container">
            <label
              class="c-checkbox-label"
              for="homeowner-check"
            >
              Homeowner Notifications Sent
            </label>
            <div class="c-checkbox-body">
              Receive real-time alerts on Slack for high/low temperatures
              and freeze warnings. Stay informed instantly.
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="c-footer-container">
      <div>
        <button
          type="button"
          class="ac-form-disconnect"
          @click="toggle(true, 'disconnect-confirm', {})"
        >
          Disconnect
        </button>
      </div>
      <div class="ac-footer-sub">
        <button
          type="button"
          class="ac-form-cancel"
          @click="toggle(false)"
        >
          Cancel
        </button>
        <button
          type="button"
          class="ac-form-button"
          @click="updatePartnerConfig"
        >
          Submit
        </button>
      </div>
    </div>
    <ProcessOverlay
      :show="updating"
      text="Processing"
    />
  </div>
</template>

<script>
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';
import apiClient from '@shared/lib/general/apiClient';

export default {
  name: 'EditIntegrationPopup',
  components: {
    ProcessOverlay,
  },
  props: {
    toggle: Function,
    update: Function,
    partnerSettings: Object,
    changeStateValue: Function,
  },
  data() {
    const {
      SLACK_LEAD_CHANNEL = '',
      SLACK_SERVICE_CHANNEL = '',
      SLACK_SYSTEM_CHANNEL = '',
      SLACK_HOMEOWNER_CHANNEL = '',
    } = this.partnerSettings;

    return {
      updating: false,
      loadingChannel: false,
      reqNextCursor: null,
      selectedChannelId: SLACK_LEAD_CHANNEL,
      lead: SLACK_LEAD_CHANNEL,
      system: SLACK_SYSTEM_CHANNEL,
      homeowner: SLACK_HOMEOWNER_CHANNEL,
      service: SLACK_SERVICE_CHANNEL,
      channelList: [],
    };
  },
  async mounted() {
    await this.getChannels();
  },
  methods: {
    handleCheckboxChange(event, field) {
      if (event.target.checked) {
        this[field] = this.selectedChannelId;
      } else {
        this[field] = '';
      }
    },
    async getChannels() {
      this.loadingChannel = true;
      try {
        let url = '/slack/channels';
        if (this.reqNextCursor) {
          url += `?nextCursor=${this.reqNextCursor}`;
        }
        const { data } = await apiClient({
          url,
          method: 'GET',
        });
        const {
          channels,
          nextCursor,
        } = data;
        this.channelList = [...this.channelList, ...channels];
        this.reqNextCursor = nextCursor;
        if (nextCursor) {
          this.getChannels();
        }
      } catch (err) {
        console.error(err);
      }
      this.loadingChannel = false;
    },
    async updatePartnerConfig() {
      this.updating = true;
      try {
        await apiClient({
          method: 'PUT',
          url: '/slack/channels/update',
          data: {
            channelId: this.selectedChannelId,
            serviceRequest: this.service !== '',
            lead: this.lead !== '',
            systemInsight: this.system !== '',
            homeownerNotification: this.homeowner !== '',
          },
        });
        const newPartnerSettings = {
          ...this.partnerSettings,
          SLACK_LEAD_CHANNEL: this.lead ? this.selectedChannelId : '',
          SLACK_SERVICE_CHANNEL: this.service ? this.selectedChannelId : '',
          SLACK_SYSTEM_CHANNEL: this.system ? this.selectedChannelId : '',
          SLACK_HOMEOWNER_CHANNEL: this.homeowner ? this.selectedChannelId : '',
        };
        this.changeStateValue('partnerSettings', newPartnerSettings);
      } catch (err) {
        console.error(err);
        this.$store.dispatch('showAlertToast', {
          message: 'There was an unexpected error updating setting. Please try again or contact support',
        });
      }
      this.updating = false;
      this.toggle(false);
    },
  },
};
</script>

<style lang="scss" scoped>

.c-footer-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;

  .ac-footer-sub {
    display: flex;
    justify-content: space-between;
    gap: 15px;
  }

  .ac-form-button {
    height: 40px;
    border-radius: 6px;
    color: white;
    border-color: #4979FF;
    background-color: #4979FF;
    font-weight: 600;
    border: 2px solid;
    padding: 12px 24px;
    height: 48px;
    width: 100%;

    &:hover {
      background-color: $NBLUE90;
    }
  }

  .ac-form-cancel {
    height: 40px;
    border-radius: 6px;
    color: #4979FF;
    border-color: #4979FF;
    background-color: white;
    font-weight: 600;
    border: 2px solid;
    padding: 12px 24px;
    height: 48px;
    width: 100%;
  }

  .ac-form-disconnect {
    height: 40px;
    border-radius: 6px;
    color: $TOMATO100;
    border-color: $TOMATO100;
    background-color: white;
    font-weight: 600;
    border: 2px solid;
    padding: 12px 24px;
    height: 48px;
    width: 100%;
  }
}

.c-container {
  display: flex;
  flex-direction: column;

  .c-header {
    font-size: 2.25rem;
    color: $N100;
    font-weight: 600;
    width: 100%;
  }

  .c-sub {
    font-size: 1.125rem;
    color: $NAVY100;
  }

  .c-sub-header {
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 8px;
    margin-top: 32px;
  }

  .c-input-container {
    width: 100%;

    .c-row-container {
      display: flex;
      width: 100%;

      .c-column-container {
        flex: 1;
        display: flex;
        flex-direction: column;

        .c-select {
          background-color: $N10;
          border-radius: 4px;
          appearance: menulist;
          padding: 4px 10px;
          height: 40px;
        }

        .c-error {
          font-size: 0.875rem;
          color: $TOMATO100;
          margin-left: 8px;
        }
      }

      .c-button-yellow {
        margin-left: 4px;
        width: auto;
        padding: 0 24px;
        height: 40px;
        border-radius: 4px;
        line-height: 1;
        color: $WHITE;
        font-weight: 500;
        font-size: 0.875rem;
        background-color: $SUNSET100;
      }
    }
  }

  .c-checkbox-container {
    display: flex;
    align-items: flex-start;
    margin-bottom: 36px;

    .c-checkbox {
      width: 24px;
      height: 24px;
    }

    .c-column-container {
      flex: 1;
      margin-left: 8px;

      .c-checkbox-label {
        font-size: 1rem;
        color: $N100;
        font-weight: 600;
        margin-bottom: 4px;
      }

      .c-checkbox-body {
        font-size: 1rem;
        color: $N100;
      }
    }
  }

  .c-button-container {
    display: flex;
    align-items: center;
    justify-content: center;

    .c-button {
      width: 430px;
      height: 48px;
      border-radius: 4px;
      line-height: 1;
      color: $WHITE;
      font-weight: 500;
      font-size: 1.125rem;
      background-color: $NBLUE100;
    }
  }
}

.c-content-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}
</style>
